import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { 
  Images, LogOut, Mail, MessageSquare, Phone, ShieldCheck, Users,
  LayoutDashboard, UserCircle, MessageCircle, PhoneCall, Image as ImageIcon, Download,
  Settings, Menu, X
} from "lucide-react";
import { isFirebaseConfigured } from "../firebase";
import { useAuth } from "../context/AuthContext";
import {
  deleteEventRegistration,
  deleteGalleryImage,
  fallbackGalleryItems,
  fetchCollectionItems,
  fetchContactDetails,
  fetchGalleryItems,
  getEventOptions,
  formatTimestamp,
  saveContactDetails,
  uploadGalleryImage,
} from "../services/siteService";

const statCards = [
  { key: "users", label: "Registered Users", icon: Users, accent: "bg-sky-500" },
  { key: "logins", label: "Total Logins", icon: ShieldCheck, accent: "bg-emerald-500" },
  { key: "enquiries", label: "Enquiry Forms", icon: MessageSquare, accent: "bg-orange-500" },
  { key: "contacts", label: "Contact Forms", icon: Mail, accent: "bg-violet-500" },
  { key: "events", label: "Event Registrations", icon: PhoneCall, accent: "bg-amber-500" },
];

const emptyContactDetails = {
  phone: "",
  email: "",
  address: "",
  hours: "",
  heroTitle: "",
  heroDescription: "",
  cityLabel: "",
  socials: [],
};

function getLocalItems(name) {
  return JSON.parse(localStorage.getItem(name) || "[]");
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildEventRegistrationsExcel(items, getEventName) {
  const headers = [
    "Token",
    "Event",
    "Event Type",
    "Child Name",
    "Child Age",
    "Parent Number",
    "Token Prefix",
    "Token Sequence",
    "Source",
    "Created At",
    "Registration ID",
  ];

  const rows = items.map((item) => [
    item.token || "",
    getEventName(item.eventType, item.eventLabel),
    item.eventType || "",
    item.childName || "",
    item.childAge || "",
    item.parentNumber || "",
    item.tokenPrefix || "",
    item.tokenSequence || "",
    item.source || "",
    formatTimestamp(item.createdAt),
    item.id || "",
  ]);

  const tableRows = [headers, ...rows]
    .map(
      (row, rowIndex) => `
        <tr>
          ${row
            .map((cell) =>
              rowIndex === 0
                ? `<th>${escapeHtml(cell)}</th>`
                : `<td>${escapeHtml(cell)}</td>`
            )
            .join("")}
        </tr>`
    )
    .join("");

  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
  <head>
    <meta charset="utf-8" />
    <meta http-equiv="Content-Type" content="application/vnd.ms-excel; charset=UTF-8" />
    <style>
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; }
      th { background: #f59e0b; color: #111827; font-weight: 700; }
    </style>
  </head>
  <body>
    <table>
      ${tableRows}
    </table>
  </body>
</html>`;
}

// Navigation Items
const navItems = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, color: "text-sky-500" },
  { id: "users", label: "Users & Logins", icon: UserCircle, color: "text-emerald-500" },
  { id: "enquiries", label: "Enquiries", icon: MessageCircle, color: "text-orange-500" },
  { id: "contacts", label: "Contact Forms", icon: PhoneCall, color: "text-violet-500" },
  { id: "events", label: "Event Registrations", icon: MessageCircle, color: "text-amber-500" },
  { id: "gallery", label: "Gallery Manager", icon: ImageIcon, color: "text-pink-500" },
  { id: "settings", label: "Contact Settings", icon: Settings, color: "text-cyan-500" },
];

const galleryCategorySuggestions = ["activities", "classroom", "learning", "kids"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { currentUser, logout, isAdmin, loading: authLoading } = useAuth();
  const storedAdminSession = useMemo(() => {
    const session = JSON.parse(
      localStorage.getItem("mockUser") || sessionStorage.getItem("mockUser") || "null"
    );

    return session?.uid === "hardcoded-admin" && session?.role === "admin" ? session : null;
  }, []);
  const canAccessAdmin = Boolean(isAdmin || storedAdminSession);
  const [activeSection, setActiveSection] = useState("dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingContact, setSavingContact] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [stats, setStats] = useState({
    users: 0,
    logins: 0,
    enquiries: 0,
    contacts: 0,
    events: 0,
  });
  const [users, setUsers] = useState([]);
  const [loginEvents, setLoginEvents] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [eventRegistrations, setEventRegistrations] = useState([]);
  const [galleryItems, setGalleryItems] = useState(fallbackGalleryItems);
  const [contactDetails, setContactDetails] = useState(emptyContactDetails);
  const [galleryForm, setGalleryForm] = useState({
    title: "",
    category: "",
    file: null,
    mediaType: "image",
    videoUrl: "",
  });

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError("");

      const [usersData, loginData, enquiryData, contactData, eventData, galleryData, contactConfig] =
        await Promise.all([
          fetchCollectionItems("users"),
          fetchCollectionItems("loginEvents", "loggedInAt"),
          fetchCollectionItems("enquiries"),
          fetchCollectionItems("contactSubmissions"),
          fetchCollectionItems("eventRegistrations"),
          fetchGalleryItems(),
          fetchContactDetails(),
        ]);

      const normalizedUsers = isFirebaseConfigured
        ? usersData
        : usersData.map((item, index) => ({ id: item.uid || `user-${index}`, ...item }));
      const normalizedLogins = isFirebaseConfigured
        ? loginData
        : loginData.map((item, index) => ({ id: `${item.uid || "login"}-${index}`, ...item }));

      setUsers(normalizedUsers);
      setLoginEvents(normalizedLogins);
      setEnquiries(enquiryData);
      setContacts(contactData);
      setEventRegistrations(eventData);
      setGalleryItems(galleryData);
      setContactDetails(contactConfig);
      setStats({
        users: normalizedUsers.length,
        logins: normalizedLogins.length,
        enquiries: enquiryData.length,
        contacts: contactData.length,
        events: eventData.length,
      });
    } catch (loadError) {
      setError(loadError.message || "Unable to load admin dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && canAccessAdmin) {
      loadDashboardData();
    }
  }, [authLoading, canAccessAdmin]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleContactChange = (event) => {
    const { name, value } = event.target;
    setContactDetails((previous) => ({ ...previous, [name]: value }));
  };

  const handleSocialChange = (index, field, value) => {
    setContactDetails((previous) => {
      const socials = [...(previous.socials || [])];
      socials[index] = { ...socials[index], [field]: value };
      return { ...previous, socials };
    });
  };

  const addSocialItem = () => {
    setContactDetails((previous) => ({
      ...previous,
      socials: [...(previous.socials || []), { label: "", icon: "", href: "" }],
    }));
  };

  const removeSocialItem = (index) => {
    setContactDetails((previous) => ({
      ...previous,
      socials: (previous.socials || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const handleSaveContact = async (event) => {
    event.preventDefault();
    setSavingContact(true);
    setError("");
    setSuccess("");

    try {
      await saveContactDetails(contactDetails);
      setSuccess("Contact page details updated.");
    } catch (saveError) {
      setError(saveError.message || "Unable to save contact page details.");
    } finally {
      setSavingContact(false);
    }
  };

  const handleGalleryUpload = async (event) => {
    event.preventDefault();
    setUploadingImage(true);
    setError("");
    setSuccess("");

    try {
      await uploadGalleryImage({
        ...galleryForm,
        category: (galleryForm.category || "gallery").trim().toLowerCase().replace(/\s+/g, "-"),
      });
      setGalleryForm({ title: "", category: "", file: null, mediaType: "image", videoUrl: "" });
      await loadDashboardData();
      setSuccess(galleryForm.mediaType === "video" ? "Gallery video added." : "Gallery image added.");
    } catch (uploadError) {
      setError(uploadError.message || "Unable to upload gallery image.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async (item) => {
    setError("");
    setSuccess("");

    try {
      await deleteGalleryImage(item);
      await loadDashboardData();
      setSuccess("Gallery image removed.");
    } catch (deleteError) {
      setError(deleteError.message || "Unable to delete gallery image.");
    }
  };

  const handleDeleteEventRegistration = async (item) => {
    setError("");
    setSuccess("");

    try {
      await deleteEventRegistration(item);
      await loadDashboardData();
      setSuccess("Event registration removed.");
    } catch (deleteError) {
      setError(deleteError.message || "Unable to delete event registration.");
    }
  };

  const handleExportEventRegistrations = () => {
    if (eventRegistrations.length === 0) {
      setError("No event registrations available to export.");
      setSuccess("");
      return;
    }

    setError("");
    setSuccess("");

    const excelContent = buildEventRegistrationsExcel(eventRegistrations, getEventName);
    const fileDate = new Date().toISOString().slice(0, 10);
    const blob = new Blob(["\uFEFF", excelContent], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `event-registrations-${fileDate}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    setSuccess("Event registrations exported as an Excel file.");
  };

  const localModeNote = !isFirebaseConfigured
    ? "Firebase env keys are not configured yet, so the dashboard is using local browser storage for demo data."
    : "Gallery media is stored directly in Firestore.";
  const getGalleryImageSource = (item) => item.imageData || item.imageUrl || "";
  const formatCategoryLabel = (value) =>
    value
      ?.split("-")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ") || "Gallery";
  const eventOptions = getEventOptions();
  const getEventName = (eventType, eventLabel) =>
    eventLabel || eventOptions.find((option) => option.value === eventType)?.label || "-";

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-28">
        <div className="mx-auto max-w-6xl rounded-3xl bg-white p-8 shadow-sm text-slate-600">
          Checking admin access...
        </div>
      </div>
    );
  }

  if (!canAccessAdmin) {
    return <Navigate to="/login" replace />;
  }

  // Render Dashboard Section
  const renderDashboardSection = () => (
    <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {statCards.map((card) => {
        const Icon = card.icon;
        return (
          <article key={card.key} className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">{card.label}</p>
                <p className="mt-3 text-3xl font-bold text-slate-900">{stats[card.key]}</p>
              </div>
              <div className={`${card.accent} rounded-2xl p-3 text-white`}>
                <Icon size={22} />
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );

  // Render Users Section
  const renderUsersSection = () => (
    <div className="space-y-8">
      <article className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Registered Users</h2>
            <p className="mt-1 text-sm text-slate-500">All accounts created for the website.</p>
          </div>
          <Users className="text-sky-500" size={22} />
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-slate-500">
              <tr>
                <th className="pb-3 font-semibold">Name</th>
                <th className="pb-3 font-semibold">Email</th>
                <th className="pb-3 font-semibold">Phone</th>
                <th className="pb-3 font-semibold">Role</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map((user) => (
                  <tr key={user.id || user.uid} className="border-t border-slate-100">
                    <td className="py-3 font-medium text-slate-900">{user.name || "-"}</td>
                    <td className="py-3 text-slate-600">{user.email || "-"}</td>
                    <td className="py-3 text-slate-600">{user.phone || "-"}</td>
                    <td className="py-3">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700">
                        {user.role || "user"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="py-6 text-center text-slate-500">
                    No users found yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </article>

      <article className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Recent Logins</h2>
            <p className="mt-1 text-sm text-slate-500">Successful sign-ins recorded for the site.</p>
          </div>
          <ShieldCheck className="text-emerald-500" size={22} />
        </div>

        <div className="mt-5 space-y-3">
          {(loginEvents.length > 0 ? loginEvents : getLocalItems("loginEvents")).slice(0, 10).map((item, index) => (
            <div key={item.id || `${item.email}-${index}`} className="rounded-2xl bg-slate-50 px-4 py-3">
              <p className="font-medium text-slate-900">{item.name || item.email || "-"}</p>
              <p className="text-sm text-slate-500">{item.email || "-"}</p>
              <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">
                {formatTimestamp(item.loggedInAt)}
              </p>
            </div>
          ))}
          {loginEvents.length === 0 && getLocalItems("loginEvents").length === 0 && (
            <div className="rounded-2xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              No login activity yet.
            </div>
          )}
        </div>
      </article>
    </div>
  );

  // Render Enquiries Section
  const renderEnquiriesSection = () => (
    <article className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Enquiry Form Details</h2>
          <p className="mt-1 text-sm text-slate-500">Submissions from popup and admission enquiry forms.</p>
        </div>
        <MessageSquare className="text-orange-500" size={22} />
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="text-slate-500">
            <tr>
              <th className="pb-3 font-semibold">Parent</th>
              <th className="pb-3 font-semibold">Child</th>
              <th className="pb-3 font-semibold">Program</th>
              <th className="pb-3 font-semibold">Contact</th>
              <th className="pb-3 font-semibold">Date</th>
            </tr>
          </thead>
          <tbody>
            {enquiries.length > 0 ? (
              enquiries.map((item) => (
                <tr key={item.id || `${item.email}-${item.createdAt}`} className="border-t border-slate-100">
                  <td className="py-3 font-medium text-slate-900">{item.parentName || "-"}</td>
                  <td className="py-3 text-slate-600">{item.childName || "-"}</td>
                  <td className="py-3 text-slate-600">{item.program || "-"}</td>
                  <td className="py-3 text-slate-600">
                    <div>{item.phoneNumber || "-"}</div>
                    <div className="text-xs text-slate-400">{item.email || "-"}</div>
                  </td>
                  <td className="py-3 text-slate-600">{formatTimestamp(item.createdAt)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="py-6 text-center text-slate-500">
                  No enquiry submissions yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </article>
  );

  // Render Contact Forms Section
  const renderContactFormsSection = () => (
    <article className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Contact Form Details</h2>
          <p className="mt-1 text-sm text-slate-500">Messages submitted from the contact page.</p>
        </div>
        <Phone className="text-violet-500" size={22} />
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="text-slate-500">
            <tr>
              <th className="pb-3 font-semibold">Parent</th>
              <th className="pb-3 font-semibold">Child Age</th>
              <th className="pb-3 font-semibold">Program</th>
              <th className="pb-3 font-semibold">Contact</th>
              <th className="pb-3 font-semibold">Date</th>
            </tr>
          </thead>
          <tbody>
            {contacts.length > 0 ? (
              contacts.map((item) => (
                <tr key={item.id || `${item.email}-${item.createdAt}`} className="border-t border-slate-100">
                  <td className="py-3 font-medium text-slate-900">{item.parentName || "-"}</td>
                  <td className="py-3 text-slate-600">{item.childAge || "-"}</td>
                  <td className="py-3 text-slate-600">{item.program || "-"}</td>
                  <td className="py-3 text-slate-600">
                    <div>{item.phoneNumber || "-"}</div>
                    <div className="text-xs text-slate-400">{item.email || "-"}</div>
                  </td>
                  <td className="py-3 text-slate-600">{formatTimestamp(item.createdAt)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="py-6 text-center text-slate-500">
                  No contact submissions yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </article>
  );

  const renderEventsSection = () => (
    <article className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Event Registration Details</h2>
          <p className="mt-1 text-sm text-slate-500">Tokens are generated separately for each event.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleExportEventRegistrations}
            disabled={eventRegistrations.length === 0}
            className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 transition hover:border-amber-300 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Download size={16} />
            Download Excel
          </button>
          <MessageCircle className="text-amber-500" size={22} />
        </div>
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="text-slate-500">
            <tr>
              <th className="pb-3 font-semibold">Token</th>
              <th className="pb-3 font-semibold">Event</th>
              <th className="pb-3 font-semibold">Child</th>
              <th className="pb-3 font-semibold">Age</th>
              <th className="pb-3 font-semibold">Parent Number</th>
              <th className="pb-3 font-semibold">Date</th>
              <th className="pb-3 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {eventRegistrations.length > 0 ? (
              eventRegistrations.map((item, index) => (
                <tr key={item.id || `${item.token}-${index}`} className="border-t border-slate-100">
                  <td className="py-3">
                    <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold tracking-wide text-amber-700">
                      {item.token || "-"}
                    </span>
                  </td>
                  <td className="py-3 text-slate-600">{getEventName(item.eventType, item.eventLabel)}</td>
                  <td className="py-3 font-medium text-slate-900">{item.childName || "-"}</td>
                  <td className="py-3 text-slate-600">{item.childAge || "-"}</td>
                  <td className="py-3 text-slate-600">{item.parentNumber || "-"}</td>
                  <td className="py-3 text-slate-600">{formatTimestamp(item.createdAt)}</td>
                  <td className="py-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleDeleteEventRegistration(item)}
                      className="rounded-full border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 transition hover:border-red-300 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="py-6 text-center text-slate-500">
                  No event registrations yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </article>
  );

  // Render Gallery Section
  const renderGallerySection = () => (
    <article className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Gallery Manager</h2>
          <p className="mt-1 text-sm text-slate-500">Upload images, paste YouTube links, and manage gallery media.</p>
        </div>
        <Images className="text-pink-500" size={22} />
      </div>

      <form onSubmit={handleGalleryUpload} className="mt-5 space-y-4">
        <input
          value={galleryForm.title}
          onChange={(event) => setGalleryForm((previous) => ({ ...previous, title: event.target.value }))}
          placeholder="Media title"
          className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-pink-500 focus:outline-none"
        />
        <select
          value={galleryForm.mediaType}
          onChange={(event) =>
            setGalleryForm((previous) => ({
              ...previous,
              mediaType: event.target.value,
              file: null,
              videoUrl: "",
            }))
          }
          className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-pink-500 focus:outline-none"
        >
          <option value="image">Image</option>
          <option value="video">YouTube Video</option>
        </select>
        <input
          value={galleryForm.category}
          onChange={(event) => setGalleryForm((previous) => ({ ...previous, category: event.target.value }))}
          list="gallery-category-suggestions"
          placeholder="Category like learning, classroom, activities"
          className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-pink-500 focus:outline-none"
        />
        <datalist id="gallery-category-suggestions">
          {galleryCategorySuggestions.map((category) => (
            <option key={category} value={category} />
          ))}
        </datalist>
        {galleryForm.mediaType === "image" ? (
          <>
            <input
              type="file"
              accept="image/*"
              onChange={(event) =>
                setGalleryForm((previous) => ({ ...previous, file: event.target.files?.[0] || null }))
              }
              className="w-full rounded-2xl border border-dashed border-slate-300 px-4 py-3 text-sm"
            />
            <p className="text-xs text-slate-500">
              The image will be saved inside Firestore as base64 data. Keep image sizes small because Firestore documents have a size limit.
            </p>
          </>
        ) : (
          <>
            <input
              value={galleryForm.videoUrl}
              onChange={(event) => setGalleryForm((previous) => ({ ...previous, videoUrl: event.target.value }))}
              placeholder="Paste YouTube link"
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-pink-500 focus:outline-none"
            />
            <p className="text-xs text-slate-500">
              Paste a valid YouTube link. Public gallery will show videos in a separate section.
            </p>
          </>
        )}
        <button
          type="submit"
          disabled={uploadingImage}
          className="w-full rounded-full bg-pink-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-pink-700 disabled:opacity-60"
        >
          {uploadingImage ? "Uploading..." : galleryForm.mediaType === "video" ? "Add Video" : "Upload Image"}
        </button>
      </form>

      <div className="mt-6 space-y-3">
        {galleryItems.map((item) => (
          <div key={item.id} className="flex items-center gap-4 rounded-2xl bg-slate-50 p-3">
            <div className="h-16 w-20 flex-shrink-0 overflow-hidden rounded-2xl bg-slate-200">
              {item.mediaType === "video" && item.thumbnailUrl ? (
                <img src={item.thumbnailUrl} alt={item.title} className="h-full w-full object-cover" />
              ) : getGalleryImageSource(item) ? (
                <img src={getGalleryImageSource(item)} alt={item.title} className="h-full w-full object-cover" />
              ) : (
                <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${item.gradient || "from-slate-300 to-slate-400"} text-2xl`}>
                  {item.emoji || "🖼️"}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-slate-900">{item.title}</p>
              <p className="text-xs uppercase tracking-wide text-slate-400">{formatCategoryLabel(item.category)}</p>
              <p className="mt-1 text-xs font-semibold text-pink-600">{item.mediaType === "video" ? "YouTube Video" : "Image"}</p>
              {(item.fileName || item.fileSize) && (
                <p className="mt-1 truncate text-xs text-slate-500">
                  {item.fileName || "Uploaded image"}{item.fileSize ? ` • ${Math.round(item.fileSize / 1024)} KB` : ""}
                </p>
              )}
            </div>
            {!String(item.id).startsWith("fallback-") && (
              <button
                type="button"
                onClick={() => handleDeleteImage(item)}
                className="rounded-full bg-red-100 px-3 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-200"
              >
                Delete
              </button>
            )}
          </div>
        ))}
      </div>
    </article>
  );

  // Render Settings Section
  const renderSettingsSection = () => (
    <article className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Contact Page Details</h2>
          <p className="mt-1 text-sm text-slate-500">Edit phone, email, address, hours, and social links.</p>
        </div>
        <Mail className="text-cyan-500" size={22} />
      </div>

      <form onSubmit={handleSaveContact} className="mt-5 space-y-4">
        <input name="phone" value={contactDetails.phone || ""} onChange={handleContactChange} placeholder="Phone number" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-500 focus:outline-none" />
        <input name="email" value={contactDetails.email || ""} onChange={handleContactChange} placeholder="Email address" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-500 focus:outline-none" />
        <input name="address" value={contactDetails.address || ""} onChange={handleContactChange} placeholder="Address" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-500 focus:outline-none" />
        <input name="hours" value={contactDetails.hours || ""} onChange={handleContactChange} placeholder="Opening hours" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-500 focus:outline-none" />
        <input name="heroTitle" value={contactDetails.heroTitle || ""} onChange={handleContactChange} placeholder="Hero title" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-500 focus:outline-none" />
        <textarea name="heroDescription" value={contactDetails.heroDescription || ""} onChange={handleContactChange} placeholder="Hero description" rows="4" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-500 focus:outline-none" />
        <input name="cityLabel" value={contactDetails.cityLabel || ""} onChange={handleContactChange} placeholder="City label" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-500 focus:outline-none" />

        <div className="space-y-3 rounded-2xl bg-slate-50 p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900">Social Links</p>
            <button type="button" onClick={addSocialItem} className="text-sm font-semibold text-cyan-700">
              Add link
            </button>
          </div>

          {(contactDetails.socials || []).map((social, index) => (
            <div key={`${social.label}-${index}`} className="grid gap-2">
              <input value={social.label || ""} onChange={(event) => handleSocialChange(index, "label", event.target.value)} placeholder="Label" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-500 focus:outline-none" />
              <input value={social.icon || ""} onChange={(event) => handleSocialChange(index, "icon", event.target.value)} placeholder="Icon text" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-500 focus:outline-none" />
              <input value={social.href || ""} onChange={(event) => handleSocialChange(index, "href", event.target.value)} placeholder="URL" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-500 focus:outline-none" />
              <button type="button" onClick={() => removeSocialItem(index)} className="justify-self-start text-sm font-semibold text-red-600">
                Remove
              </button>
            </div>
          ))}
        </div>

        <button
          type="submit"
          disabled={savingContact}
          className="w-full rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
        >
          {savingContact ? "Saving..." : "Save Contact Details"}
        </button>
      </form>
    </article>
  );

  // Render active section content
  const renderActiveSection = () => {
    switch (activeSection) {
      case "dashboard":
        return renderDashboardSection();
      case "users":
        return renderUsersSection();
      case "enquiries":
        return renderEnquiriesSection();
      case "contacts":
        return renderContactFormsSection();
      case "gallery":
        return renderGallerySection();
      case "events":
        return renderEventsSection();
      case "settings":
        return renderSettingsSection();
      default:
        return renderDashboardSection();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-28">
        <div className="mx-auto max-w-6xl rounded-3xl bg-white p-8 shadow-sm text-slate-600">
          Loading dashboard...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="rounded-full bg-slate-950 p-3 text-white shadow-lg"
        >
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Sidebar Navigation */}
      <aside className={`
        fixed left-0 top-0 z-40 h-full w-72 bg-white shadow-xl transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
      `}>
        <div className="flex h-full flex-col">
          {/* Sidebar Header */}
          <div className="border-b border-slate-100 p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 p-2">
                <LayoutDashboard className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Admin Panel</h2>
                <p className="text-xs text-slate-500">
                  {currentUser?.name || currentUser?.email || storedAdminSession?.name || storedAdminSession?.email}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 space-y-1 p-4">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveSection(item.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200
                    ${isActive 
                      ? "bg-slate-100 text-slate-900" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }
                  `}
                >
                  <Icon size={18} className={isActive ? item.color : "text-slate-400"} />
                  <span>{item.label}</span>
                  {isActive && (
                    <div className="ml-auto h-1.5 w-1.5 rounded-full bg-slate-900" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Logout Button */}
          <div className="border-t border-slate-100 p-4">
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-red-600 transition hover:bg-red-50"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen px-4 py-8 transition-all duration-300">
        <div className="mx-auto max-w-6xl space-y-8">
          {/* Header Section */}
          <section className="rounded-[28px] bg-slate-950 px-6 py-8 text-white shadow-xl sm:px-8">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm uppercase tracking-[0.25em] text-cyan-300">Admin Dashboard</p>
                <h1 className="mt-3 text-3xl font-bold sm:text-4xl">
                  {navItems.find(item => item.id === activeSection)?.label || "Dashboard"}
                </h1>
                <p className="mt-3 max-w-2xl text-sm text-slate-300 sm:text-base">
                  Manage and monitor your {activeSection} from this centralized panel.
                </p>
                {/* {localModeNote && (
                  <p className="mt-4 rounded-2xl bg-amber-400/15 px-4 py-3 text-sm text-amber-100">
                    {localModeNote}
                  </p>
                )} */}
              </div>

              {/* Mobile-friendly stats or info */}
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-white/10 px-4 py-2 text-sm">
                  {new Date().toLocaleDateString()}
                </div>
              </div>
            </div>
          </section>

          {/* Error/Success Messages */}
          {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
          {success && <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</div>}

          {/* Dynamic Content */}
          {renderActiveSection()}
        </div>
      </main>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
}
